import {
  Badge,
  Box,
  Checkbox,
  Icon,
  InlineStack,
  Link,
  PlainButton,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useStore } from '@nanostores/react';
import { useEffect, useMemo, useState } from 'react';
import { accountStore } from '@/services/auth/store';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { chevronIcon, scopeRow, scopeRowFirst } from './index.css';

// Labels live client-side for designer/reviewer context. /v1/oauth2/scopes
// is the source of truth for programmatic clients.

interface SelectScopesProps {
  // App name shown in the "Allow {name} to:" heading.
  clientName?: string;
  // Scopes from the OAuth request. Consent can narrow but not widen.
  requestedScopes: string[];
  // Lifted granted set, so the parent can POST it on submit.
  selectedScopes: string[];
  onSelectScopesChange: (scopes: string[]) => void;
}

// Keep in sync with services/domains/service_accounts/scopes.go.
const SCOPE_READ = 'read';
const SCOPE_READ_SENSITIVE = 'read:sensitive';
const SCOPE_WRITE = 'write';
const SCOPE_WRITE_LIVE = 'write:live';
const SCOPE_CONFIGURE = 'configure';

export const KNOWN_SCOPES: ReadonlySet<string> = new Set([
  SCOPE_READ,
  SCOPE_READ_SENSITIVE,
  SCOPE_WRITE,
  SCOPE_WRITE_LIVE,
  SCOPE_CONFIGURE,
]);

interface ScopeRowSpec {
  scope: string;
  label: string;
  description: string;
  // Required scopes can't be unticked (server-side inheritance assumes them).
  required?: boolean;
}

const ROWS: ScopeRowSpec[] = [
  {
    scope: SCOPE_READ,
    label: 'Read workspace data',
    description: 'Campaigns, segments, profiles, content, and delivery metrics.',
    required: true,
  },
  {
    scope: SCOPE_READ_SENSITIVE,
    label: 'Read sensitive profile data',
    description:
      "Read profile attributes classified as PII, including emails, phone numbers, and any fields you've marked sensitive. Handle with the same care as production data — these values can identify real people.",
  },
  {
    scope: SCOPE_CONFIGURE,
    label: 'Configure workspace settings',
    description:
      "Integrations, webhooks, channel settings, and other workspace configuration. Doesn't include editing content or sending messages.",
  },
  {
    scope: SCOPE_WRITE,
    label: 'Create and edit drafts',
    description:
      'Create, edit, and delete drafts: campaigns, segments, newsletters, templates, profiles, and other content.',
  },
  {
    scope: SCOPE_WRITE_LIVE,
    label: 'Send messages and manage subscriptions',
    description:
      'Push content live: send messages, manage subscriptions and suppressions, enable subscription centers.',
  },
];

const PRIVACY_AI_SETTINGS_PATH = '/settings/ai';

// Renders requested scopes as an accordion list. Unknown scopes are
// silently dropped on init — server rejects them upstream.
export function SelectScopes({ clientName, requestedScopes, selectedScopes, onSelectScopesChange }: SelectScopesProps) {
  const liveEditsAllowed = useFeatureFlag(AccountFeatureFlag.AllowAgentEditLiveData);
  const { hasPermission } = usePermissions();
  const account = useStore(accountStore);
  // Account admins implicitly have every workspace permission — mirror the
  // server's stripUngrantableScopes IsAdmin bypass so the checkbox matches.
  const canReadSensitive = account?.admin === true || hasPermission('journeys/sensitive_data/view') === true;

  const requestedSet = useMemo(() => new Set(requestedScopes), [requestedScopes]);

  // Initial selection mirrors the request, filtered to known scopes and the
  // account stance. read:sensitive is excluded so the row renders unticked —
  // PII access is opt-in even when the client asks for it. canReadSensitive
  // is intentionally NOT a dependency: re-running this effect when the
  // account loads in would clobber the user's toggles.
  useEffect(() => {
    const initial = requestedScopes.filter((s) => {
      if (!KNOWN_SCOPES.has(s)) return false;
      if (s === SCOPE_WRITE_LIVE && !liveEditsAllowed) return false;
      if (s === SCOPE_READ_SENSITIVE) return false;
      return true;
    });
    onSelectScopesChange(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedScopes.join(' '), liveEditsAllowed]);

  const visibleRows = useMemo(() => ROWS.filter((row) => requestedSet.has(row.scope)), [requestedSet]);

  if (visibleRows.length === 0) {
    return null;
  }

  const selectedSet = new Set(selectedScopes);

  const toggle = (scope: string, checked: boolean) => {
    const next = new Set(selectedSet);
    if (checked) next.add(scope);
    else next.delete(scope);
    onSelectScopesChange(Array.from(next));
  };

  const displayName = clientName != null && clientName.length > 0 ? clientName : 'this app';

  return (
    <Stack gap="100">
      <Text size="s" color="base">
        Allow{' '}
        <Text as="span" size="s" weight="medium" color="base">
          {displayName}
        </Text>{' '}
        to:
      </Text>

      <Stack gap="0">
        {visibleRows.map((row, index) => (
          <ScopeRow
            key={row.scope}
            spec={row}
            isFirst={index === 0}
            blockedReason={blockedReason(row.scope, liveEditsAllowed, canReadSensitive)}
            isChecked={row.required === true || selectedSet.has(row.scope)}
            onToggle={(checked) => toggle(row.scope, checked)}
          />
        ))}
      </Stack>
    </Stack>
  );
}

type BlockedReason = { kind: 'privacy-ai-settings' } | { kind: 'sensitive-data-permission' } | null;

function blockedReason(scope: string, liveEditsAllowed: boolean, canReadSensitive: boolean): BlockedReason {
  if (scope === SCOPE_WRITE_LIVE && !liveEditsAllowed) return { kind: 'privacy-ai-settings' };
  if (scope === SCOPE_READ_SENSITIVE && !canReadSensitive) return { kind: 'sensitive-data-permission' };
  return null;
}

interface ScopeRowProps {
  spec: ScopeRowSpec;
  isFirst: boolean;
  blockedReason: BlockedReason;
  isChecked: boolean;
  onToggle: (checked: boolean) => void;
}

function ScopeRow({ spec, isFirst, blockedReason, isChecked, onToggle }: ScopeRowProps) {
  const isBlocked = blockedReason != null;
  const [expanded, setExpanded] = useState(false);
  const id = `mcp-scope-${spec.scope.replace(/[^\da-z]+/gi, '-')}`;
  const labelId = `${id}-label`;
  const detailsId = `${id}-details`;
  const isLocked = spec.required === true || isBlocked;

  return (
    <Box className={isFirst ? scopeRowFirst : scopeRow}>
      <InlineStack alignBlock="center" gap="100" wrap="nowrap" py="100">
        <Box flexShrink="0">
          <Checkbox
            id={id}
            ariaLabelledby={labelId}
            ariaDescribedby={detailsId}
            isChecked={isChecked}
            isDisabled={isLocked}
            onCheckedChange={(checked) => onToggle(checked === true)}
          />
        </Box>
        <InlineStack alignBlock="center" gap="100" flexGrow="1" wrap="wrap">
          <Text id={labelId} size="s" weight="medium" color={isBlocked ? 'subtle' : 'base'}>
            {spec.label}
          </Text>
          {spec.required === true ? <Badge isOutline>REQUIRED</Badge> : null}
        </InlineStack>
        <Box flexShrink="0">
          <PlainButton
            type="button"
            aria-expanded={expanded}
            aria-controls={detailsId}
            // Scope-specific label so screen readers can tell rows apart —
            // a single generic "Show details" repeated five times collapses
            // the list into noise for AT users.
            aria-label={`${expanded ? 'Hide' : 'Show'} ${spec.label} details`}
            onClick={() => setExpanded((v) => !v)}
            p="025"
            cursor="pointer"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            color="subtle"
          >
            <span className={chevronIcon} data-expanded={expanded}>
              <Icon name="chevron-down" size="xs" />
            </span>
          </PlainButton>
        </Box>
      </InlineStack>
      {expanded ? (
        <Box id={detailsId} pl="400" pr="100" pb="150">
          <Stack gap="100">
            <Text size="xs" color="subtle">
              {spec.description}
            </Text>
            <BlockedNotice reason={blockedReason} />
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

type BlockedNoticeProps = { reason: BlockedReason };

function BlockedNotice({ reason }: BlockedNoticeProps) {
  if (reason == null) return null;
  if (reason.kind === 'privacy-ai-settings') {
    return (
      <InlineStack alignBlock="center" gap="050" wrap="wrap">
        <Icon name="lock-secure" size="xs" color="subtle" />
        <Text size="xs" color="subtle">
          Blocked by your Privacy & AI settings.
        </Text>
        <Link href={PRIVACY_AI_SETTINGS_PATH} variant="secondary">
          Review settings
        </Link>
      </InlineStack>
    );
  }
  return (
    <InlineStack alignBlock="center" gap="050" wrap="wrap">
      <Icon name="lock-secure" size="xs" color="subtle" />
      <Text size="xs" color="subtle">
        Your role doesn't include sensitive data access.
      </Text>
    </InlineStack>
  );
}
